import { useCallback, useRef, useEffect } from 'react';
import { useNodeStore } from '../stores/nodeStore';
import { useProjectStore } from '../stores/projectStore';
import { useTaskStore } from '../stores/taskStore';
import { AppNode, NodeType, NodeStatus, MultiAngleParams } from '../types';
import { generateApi, GenerateRequest } from '../services/generateApi';
import { isBackendAssetUrl, saveSingleAsset } from '../utils/assetUtils';
import { toast } from 'sonner';

export function useMultiAngleGenerator(
    saveHistory: () => void,
    handleAssetGenerated: (type: 'image' | 'video' | 'audio', src: string, title: string) => void,
) {
    const nodes = useNodeStore((s) => s.nodes);
    const addNode = useNodeStore((s) => s.addNode);
    const addConnection = useNodeStore((s) => s.addConnection);
    const updateNode = useNodeStore((s) => s.updateNode);
    const currentProjectId = useProjectStore((s) => s.currentProjectId);
    const addTaskWithRequest = useTaskStore((s) => s.addTaskWithRequest);
    const removeTask = useTaskStore((s) => s.removeTask);
    const nodesRef = useRef(nodes);
    useEffect(() => { nodesRef.current = nodes; }, [nodes]);

    const handleGenerateMultiAngle = useCallback(async (sourceNodeId: string, params: {
        prompt: string;
        imageUrl: string;
        model?: string;
        aspectRatio?: string;
        multiAngleParams: MultiAngleParams;
    }) => {
        const sourceNode = nodesRef.current.find(n => n.id === sourceNodeId);
        if (!sourceNode) return;

        const newNode: AppNode = {
            id: `n-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
            type: NodeType.IMAGE_GENERATOR,
            x: sourceNode.x + 500,
            y: sourceNode.y,
            ...(sourceNode.width !== undefined && sourceNode.height !== undefined
                ? { width: sourceNode.width, height: sourceNode.height }
                : { width: 420 }),
            title: '多角度',
            status: NodeStatus.WORKING,
            data: {
                prompt: params.prompt,
                model: params.model || 'wavespeed-qwen-image-edit-multiple-angles',
                aspectRatio: params.aspectRatio || '1:1',
            },
            inputs: [sourceNodeId],
        };

        try {
            saveHistory();
            addNode(newNode);
            addConnection(sourceNodeId, newNode.id);

            const request: GenerateRequest = {
                modelId: params.model || 'wavespeed-qwen-image-edit-multiple-angles',
                prompt: params.prompt,
                mode: 'img2img',
                inputImages: [params.imageUrl],
                params: {
                    aspectRatio: params.aspectRatio || '1:1',
                    multiAngleParams: params.multiAngleParams,
                },
                feature: 'multi-angle',
            };

            const asyncResult = await generateApi.generateAsync(request);
            const taskId = asyncResult.taskId;

            addTaskWithRequest(taskId, newNode.id, NodeType.IMAGE_GENERATOR, request as Record<string, any>);
            updateNode(newNode.id, { taskId });

            const statusResult = await generateApi.pollTaskUntilComplete(taskId);

            if (statusResult.status === 'failed') {
                throw new Error(statusResult.error || '多角度图片生成失败');
            }

            const resultData = statusResult.result;
            if (!resultData?.results || resultData.results.length === 0) {
                throw new Error('未收到生成的多角度图片');
            }

            let finalUrl: string = resultData.results[0]?.url || '';
            if (!finalUrl) throw new Error('未收到生成的多角度图片');
            if (currentProjectId && !isBackendAssetUrl(finalUrl)) {
                finalUrl = await saveSingleAsset(currentProjectId, finalUrl, 'multi-angle.png', {
                    generator_node_id: newNode.id,
                    generator_type: 'multi-angle',
                    prompt: params.prompt,
                    model: params.model || '',
                    multiAngleParams: JSON.stringify(params.multiAngleParams),
                });
            }

            updateNode(newNode.id, {
                image: finalUrl,
            }, undefined, undefined, NodeStatus.SUCCESS);

            removeTask(taskId);
            handleAssetGenerated('image', finalUrl, params.prompt || 'Multi-angle View');
            toast.success('多角度图片生成成功');
        } catch (e: any) {
            console.error('多角度图片生成失败:', e);
            const pendingTask = useTaskStore.getState().getTaskByNodeId(newNode.id);
            if (pendingTask) removeTask(pendingTask.taskId);
            updateNode(newNode.id, { error: e.message }, undefined, undefined, NodeStatus.ERROR);
            toast.error('多角度图片生成失败', { description: e.message, duration: 6000 });
        }
    }, [addNode, addConnection, updateNode, handleAssetGenerated, currentProjectId, saveHistory, addTaskWithRequest, removeTask]);

    return { handleGenerateMultiAngle };
}
